import React, { useState, useEffect } from 'react';
import { useMSGReadOnly } from '../../../contexts/ReadOnlyContext';
import { UserRole } from '@/services/rolesService';
import rolesService from '@/services/rolesService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Pencil, Save, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenancy } from '@/contexts/TenancyContext';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { TeamsCombobox, TeamsProvider } from '@/components/ui/TeamsCombobox';
import { ISMSRoleCombobox } from '@/components/ui/ISMSRoleCombobox';
import { ISMSCommitteeCombobox } from '@/components/ui/ISMSCommitteeCombobox';
import { useMSGVersion } from '../../../hooks/useMSGVersion';

interface RolesTableProps {
  className?: string;
}

export function RolesTable({ className }: RolesTableProps) {
  const { userIdToName } = useTenancy();
  const { isReadOnly } = useMSGReadOnly();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { incrementVersion } = useMSGVersion();
  
  // Sort roles by user display name
  const sortedRoles = [...roles].sort((a, b) => {
    const nameA = userIdToName[a.userId]?.toLowerCase() || '';
    const nameB = userIdToName[b.userId]?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<UserRole> | null>(null);
  const [availableRoles, setAvailableRoles] = useState<{
    ismsRoles: { roleName: string; id: string }[];
    ismsCommitteeRoles: { roleName: string; id: string }[];
  }>({ 
    ismsRoles: [{ roleName: 'No roles available', id: 'no-roles' }], 
    ismsCommitteeRoles: [{ roleName: 'No committees available', id: 'no-committees' }] 
  });

  // Load roles and available options
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load user roles
        const userRoles = await rolesService.getAllUserRoles();
        console.log('userRoles', userRoles);
        setRoles(userRoles);
        
        const loadAvailableRoles = async () => {
          try {
            const roles = await rolesService.getISMSRoles();
            // Ensure we have at least one role and committee
            const ismsRoles = roles.ismsRoles.length > 0 
              ? roles.ismsRoles 
              : [{ roleName: 'No roles available', id: 'no-roles' }];
            const ismsCommitteeRoles = roles.ismsCommitteeRoles.length > 0 
              ? roles.ismsCommitteeRoles 
              : [{ roleName: 'No committees available', id: 'no-committees' }];
            
            setAvailableRoles({
              ismsRoles,
              ismsCommitteeRoles
            });
          } catch (err) {
            console.error('Error loading available roles:', err);
            toast.error('Failed to load available roles');
          }
        };

        loadAvailableRoles();
        
        setError(null);
      } catch (err) {
        console.error('Error loading roles:', err);
        setError('Failed to load roles. Please try again later.');
        toast.error('Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time updates
    const unsubscribe = rolesService.onUserRolesChange((updatedRoles) => {
      setRoles(updatedRoles);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleEdit = (role: UserRole) => {
    setEditingId(role.id);
    setEditData({ 
      ...role,
      // Ensure we have valid values for the selects
      ismsRole: role.ismsRole || 'N/A',
      ismsCommittee: role.ismsCommittee || 'N/A'
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleSave = async () => {
    if (!editingId || !editData) return;

    try {
      const oldRole = roles.find(r => r.id === editingId) || null;
      await rolesService.updateUserRole(editingId, {
        teamName: editData.teamName || '',
        ismsRole: editData.ismsRole || '',
        ismsCommittee: editData.ismsCommittee || '',
      });

      toast.success('Role updated successfully');

      // Increment MSG version with snapshot data
      try {
        await incrementVersion(
          'role_update',
          `Updated role for ${oldRole ? (userIdToName[oldRole.userId] || oldRole.userId) : editingId}`,
          {
            roleId: editingId,
            oldData: oldRole,
            newData: {
              ...oldRole,
              teamName: editData.teamName || '',
              ismsRole: editData.ismsRole || '',
              ismsCommittee: editData.ismsCommittee || ''
            }
          }
        );
      } catch (versionErr) {
        console.error('[RolesTable] Version update failed:', versionErr);
      }

      setEditingId(null);
      setEditData(null);
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const handleInputChange = (field: keyof UserRole, value: string) => {
    if (!editData) return;
    setEditData({
      ...editData,
      [field]: value,
    });
  };

  const handleTeamSelect = (teams: string[]) => {
    // TeamsCombobox is passing team names in the array
    // We take the first selected team name (or empty string if none selected)
    handleInputChange('teamName', teams[0] || '');
  };

  const handleUserSelect = (userId: string) => {
    handleInputChange('userId', userId);
  };

  const handleRoleSelect = (roleId: string) => {
    if (!editData) return;
    setEditData({
      ...editData,
      ismsRole: roleId,
    });
  };

  const handleRoleDeselect = () => {
    if (!editData) return;
    setEditData({
      ...editData,
      ismsRole: '',
    });
  };

  const handleCommitteeSelect = (committeeId: string) => {
    if (!editData) return;
    setEditData({
      ...editData,
      ismsCommittee: committeeId,
    });
  };

  const handleCommitteeDeselect = () => {
    if (!editData) return;
    setEditData({
      ...editData,
      ismsCommittee: '',
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-4 border border-destructive rounded-md">
        {error}
      </div>
    );
  }

  if (roles.length === 0) {
    return <div className="text-muted-foreground p-4">No roles found</div>;
  }

  return (
    <div className={`rounded-md ${className}`}>
      <Table className="w-full table">
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Team</TableHead>
            <TableHead>MSG Role</TableHead>
            <TableHead>MSG Committee</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRoles.map((role) => (
            <TableRow key={role.id}>
              <TableCell className="font-medium">
                {editingId === role.id ? (
                  <SingleUserCombobox
                    selected={editData?.userId || ''}
                    setSelected={handleUserSelect}
                    className="w-full"
                    readOnly
                  />
                ) : (
                  userIdToName[role.userId]
                )}
              </TableCell>
              <TableCell>
                {editingId === role.id ? (
                  <div className="w-full">
                    <TeamsProvider>
                      <TeamsCombobox
                        selected={editData?.teamName ? [editData.teamName] : []}
                        setSelected={handleTeamSelect}
                        singleSelect={true}
                        placeholder="Select a team..."
                        className="w-full"
                      />
                    </TeamsProvider>
                  </div>
                ) : (
                  <span>{(role as any).teamName || (role as any).team || 'No team assigned'}</span>
                )}
              </TableCell>
              <TableCell>
                {editingId === role.id ? (
                  <ISMSRoleCombobox
                    selected={editData.ismsRole ? [editData.ismsRole] : []}
                    setSelected={(values) => values[0] && handleRoleSelect(values[0])}
                    singleSelect={true}
                    placeholder="Select a role..."
                    className="w-full"
                    roles={availableRoles.ismsRoles.map(role => ({
                      id: role.roleName,
                      roleName: role.roleName
                    }))}
                    onRoleSelect={handleRoleSelect}
                    onRoleDeselect={handleRoleDeselect}
                  />
                ) : (
                  role.ismsRole
                )}
              </TableCell>
              <TableCell>
                {editingId === role.id ? (
                  <ISMSCommitteeCombobox
                    selected={editData.ismsCommittee ? [editData.ismsCommittee] : []}
                    setSelected={(values) => values[0] && handleCommitteeSelect(values[0])}
                    singleSelect={true}
                    placeholder="Select a committee..."
                    className="w-full"
                    committees={availableRoles.ismsCommitteeRoles.map(committee => ({
                      id: committee.roleName,
                      roleName: committee.roleName
                    }))}
                    onCommitteeSelect={handleCommitteeSelect}
                    onCommitteeDeselect={handleCommitteeDeselect}
                  />
                ) : (
                  role.ismsCommittee
                )}
              </TableCell>
              <TableCell>
                {editingId === role.id ? (
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSave}
                      className="h-8 px-2"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="h-8 px-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  !isReadOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(role)}
                      className="h-8 px-2"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
